if (!customElements.get('product-options')) {
  customElements.define(
    'product-options',
    class ProductOptions extends HTMLElement {
      connectedCallback() {
        if (!window.Northyard?.ProductState || !this.querySelector('[data-product-state]')) return;
        this.lifecycle?.abort();
        this.lifecycle = new AbortController();
        this.frames = new Set();
        this.section = this.closest('[data-product-section]');
        this.form = this.querySelector('form');
        this.gallery = this.section.querySelector('[data-product-gallery]');
        this.mediaContainer = this.section.querySelector('[data-product-media]');
        this.mediaThumbnails = this.section.querySelector('[data-product-media-thumbnails]');
        this.mediaStatus = this.section.querySelector('[data-product-media-status]');
        this.mediaViewer = this.section.querySelector('[data-product-media-viewer]');
        this.mediaViewerStage = this.section.querySelector('[data-product-media-viewer-stage]');
        this.mediaViewerPrevious = this.section.querySelector('[data-product-media-viewer-previous]');
        this.mediaViewerNext = this.section.querySelector('[data-product-media-viewer-next]');
        this.mediaViewerCount = this.section.querySelector('[data-product-media-viewer-count]');
        this.mediaViewerIndex = 0;
        this.mediaViewerOpener = null;
        this.mediaUpdateToken = (this.mediaUpdateToken || 0) + 1;
        this.payload ||= JSON.parse(this.querySelector('[data-product-state]').textContent);
        this.selectionToken = (this.selectionToken || 0) + 1;
        this.selectionPending = false;
        this.colorOptionIndex = Number(this.dataset.colorOptionIndex);
        this.sizeOptionIndex = Number(this.dataset.sizeOptionIndex ?? -1);
        this.listen(this, 'change', this.onOptionChange.bind(this));
        this.listen(this, 'click', (event) => {
          const alternative = event.target.closest('[data-stock-color]');
          if (alternative && !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
            const commerce = window.Northyard;
            if (this.selectionPending || this.cartSubmitting || commerce.cartMutationPending || commerce.cartEditPending) {
              event.preventDefault();
              return;
            }
            const options = this.selectedOptions();
            const selected = Object.fromEntries(this.payload.options.map(option => [option.name, options[option.position - 1]]));
            const choice = commerce.ProductState.alternativeSelection(this.payload, selected, alternative.dataset.stockColor);
            const definition = this.payload.options.find(option => option.name === choice?.name);
            const input = definition && [...this.querySelectorAll('[data-option-index="' + (definition.position - 1) + '"] input[type="radio"]')]
              .find(entry => entry.value === choice.value && !entry.disabled && !entry.dataset.productUrl);
            if (input) {
              event.preventDefault();
              input.checked = true;
              input.focus({ preventScroll: true });
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
            return;
          }
          if (event.target.closest('[data-product-state-retry]') && this.lastOptionInput) {
            this.onOptionChange({ target: this.lastOptionInput, retry: true });
          }
        });
        if (this.form) this.listen(this.form, 'submit', this.onSubmit.bind(this));
        for (const type of ['northyard:cart-busy', 'northyard:cart-state']) {
          this.listen(document, type, () => this.syncPurchaseState());
        }
        this.mobileColorJump = this.section.querySelector('[data-mobile-color-jump]');
        if (this.mobileColorJump) this.listen(this.mobileColorJump, 'click', this.onMobileColorJump.bind(this));
        this.mobileBar = this.section.querySelector('[data-product-mobile-bar]');
        this.primaryAction = this.section.querySelector('[data-product-primary-action]');
        this.bindQuantityControl();
        this.bindMobileBarVisibility();
        this.bindDescriptionDisclosure();
        this.visibleMedia().forEach((item) => { item.dataset.mediaRole = this.mediaRoleFromAlt(item.dataset.mediaAlt); });
        this.bindMediaGallery();
        this.bindMediaViewer();

        const selectedOptions = this.selectedOptions();
        this.updateAvailability(this.payload);
        this.updateMediaRatio(this.visibleMedia()[0]);
        this.rebuildMediaThumbnails(this.visibleMedia());
        this.updateMediaProgress();
        this.updateMobileColor(this.selectedColor(selectedOptions));
        this.updateMobileSize(selectedOptions);
        if (!this.payload.variant) this.updateVariant(null, window.Northyard.ProductState.selectedIds(this.payload));
        this.updateStockAlternatives();
        this.syncNativeOptions();
        this.querySelectorAll('[data-option-index] input[type="radio"]').forEach(input => { input.disabled = false; });
        this.setAttribute('data-options-ready', '');
        if (this.resumeSelection?.isConnected) this.onOptionChange({ target: this.resumeSelection, retry: true });
        else if (this.resumeMedia) this.startMediaUpdate(this.resumeMedia, this.selectionToken);
        this.resumeSelection = null;
        this.resumeMedia = null;
      }

      disconnectedCallback() {
        this.removeAttribute('data-options-ready');
        this.querySelectorAll('[data-option-index] input[type="radio"]').forEach(input => { input.disabled = true; });
        this.resumeSelection = this.selectionPending ? this.lastOptionInput : null;
        this.resumeMedia = this.pendingMediaUpdate?.payload;
        this.lifecycle?.abort();
        this.frames?.forEach(frame => cancelAnimationFrame(frame));
        this.frames?.clear();
        clearTimeout(this.mobileFocusTimer);
        ++this.selectionToken;
        this.cancelMediaUpdate();
        this.mediaResize?.disconnect();
        if (this.mediaViewer?.open) {
          this.mediaViewer.close();
          this.mediaViewerStage?.replaceChildren();
          if (!document.querySelector('dialog[open]')) document.body.classList.remove('is-overlay-open');
        }
      }

      listen(target, type, handler, options = {}) {
        target?.addEventListener(type, handler, { ...options, signal: this.lifecycle.signal });
      }

      requestFrame(callback) {
        const frame = requestAnimationFrame(() => {
          this.frames.delete(frame);
          if (this.isConnected) callback();
        });
        this.frames.add(frame);
        return frame;
      }

      bindQuantityControl() {
        const control = this.form && this.form.querySelector('[data-quantity-control]');
        const input = control && control.querySelector('input[name="quantity"]');
        if (!control || !input) return;
        const update = (direction) => {
          const minimum = Math.max(1, Number(input.min) || 1);
          const current = Math.max(minimum, Number(input.value) || minimum);
          input.value = String(Math.max(minimum, current + direction));
          input.dispatchEvent(new Event('change', { bubbles: true }));
          input.focus({ preventScroll: true });
        };
        this.listen(control.querySelector('[data-quantity-decrease]'), 'click', () => update(-1));
        this.listen(control.querySelector('[data-quantity-increase]'), 'click', () => update(1));
        this.listen(input, 'change', () => {
          input.value = String(Math.max(1, Number(input.value) || 1));
        });
      }

      bindMobileBarVisibility() {
        if (!this.mobileBar || !this.primaryAction) return;
        let frame = 0;
        const update = () => {
          frame = 0;
          const mobile = window.matchMedia('(max-width: 749px)').matches;
          const header = document.querySelector('[data-site-header]');
          const safeTop = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
          const passedPrimaryAction = this.primaryAction.getBoundingClientRect().bottom <= safeTop + 8;
          const visible = mobile && passedPrimaryAction;
          this.mobileBar.classList.toggle('is-visible', visible);
          this.mobileBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
        };
        const schedule = () => {
          if (frame) return;
          frame = this.requestFrame(update);
        };
        this.listen(window, 'scroll', schedule, { passive: true });
        this.listen(window, 'resize', schedule, { passive: true });
        update();
      }

      bindDescriptionDisclosure() {
        const disclosure = this.section.querySelector('[data-product-description-disclosure]');
        if (!disclosure) return;
        const desktop = window.matchMedia('(min-width: 750px)');
        const update = () => { disclosure.open = desktop.matches; };
        update();
        this.listen(desktop, 'change', update);
      }

      selectedOptions() {
        const optionCount = this.payload.options.length;
        const selected = new Array(optionCount).fill(null);
        Array.from(this.querySelectorAll('[data-option-index]')).forEach((fieldset) => {
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          if (checked) selected[Number(fieldset.dataset.optionIndex)] = checked.value;
        });
        return selected;
      }

      syncNativeOptions() {
        const commerce = window.Northyard;
        const chosen = Object.fromEntries(this.payload.options.map(option => [option.name, this.selectedOptions()[option.position - 1]]));
        const ids = commerce.ProductState.selectedIds(this.payload, chosen);
        this.querySelectorAll('[data-option-index] input[type="radio"]').forEach(input => {
          const link = input.nextElementSibling?.querySelector('[data-option-native]');
          if (!link) return;
          const values = [...ids];
          values[Number(input.closest('[data-option-index]').dataset.optionIndex)] = input.dataset.optionValueId;
          const sibling = input.dataset.productUrl && new URL(input.dataset.productUrl, location.origin).pathname !== new URL(this.dataset.productUrl, location.origin).pathname;
          const url = sibling ? new URL(input.dataset.productUrl, location.origin) : commerce.ProductState.selectionUrl(this.dataset.productUrl, { optionValues: values });
          const preview = new URL(location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          link.href = url.href;
          link.setAttribute('aria-label', input.value + (input.nextElementSibling.classList.contains('is-unavailable') ? ', ' + this.section.querySelector('[data-add-to-cart]').dataset.soldOutLabel : ''));
          if (input.checked) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });
      }

      async onOptionChange(event) {
        const selectedInput = event.target.closest('input[type="radio"]');
        if (!selectedInput) return;
        this.lastOptionInput = selectedInput;
        if (selectedInput.dataset.productUrl) {
          const nextUrl = new URL(selectedInput.dataset.productUrl, window.location.origin);
          if (nextUrl.pathname !== window.location.pathname) {
            window.location.assign(nextUrl.href);
            return;
          }
        }
        const token = ++this.selectionToken;
        ++this.mediaUpdateToken;
        this.cancelMediaUpdate();
        this.selectionPending = true;
        this.updateStockAlternatives();
        this.setMediaViewerFeedback();
        const selectedOptions = this.selectedOptions();
        const selected = Object.fromEntries(this.payload.options.map((option) => [option.name, selectedOptions[option.position - 1]]));
        const optionValues = window.Northyard.ProductState.selectedIds(this.payload, selected);
        const optionIndex = Number(selectedInput.closest('[data-option-index]').dataset.optionIndex);
        this.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
          input.closest('fieldset').querySelectorAll('[data-selected-option]').forEach((label) => { label.textContent = input.value; });
        });
        this.syncNativeOptions();
        this.section.querySelector('[data-variant-id]').value = '';
        this.section.querySelectorAll('[data-add-to-cart]').forEach((button) => {
          button.disabled = true;
          button.textContent = this.dataset.loadingLabel;
        });
        this.setAttribute('aria-busy', 'true');
        const errorNode = this.querySelector('[data-product-form-error]');
        if (errorNode) errorNode.hidden = true;
        this.updateMobileColor(this.selectedColor(selectedOptions));
        this.updateMobileSize(selectedOptions);
        const analytics = window.Northyard;
        if (!event.retry && optionIndex === this.colorOptionIndex) {
          analytics.emit('color_select', { product_id: this.dataset.productId, color: selectedInput.value, location: 'product_page' });
        } else if (!event.retry && optionIndex === this.sizeOptionIndex) {
          analytics.emit('size_select', { product_id: this.dataset.productId, size: selectedInput.value, location: 'product_page' });
        }
        try {
          const payload = await analytics.ProductState.request(this.dataset.productUrl, { optionValues, fresh: true });
          if (token !== this.selectionToken || !this.isConnected) return;
          this.payload = payload;
          this.selectionPending = false;
          this.updateAvailability(payload);
          this.syncNativeOptions();
          this.updateVariant(payload.variant, optionValues);
          this.startMediaUpdate(payload, token);
        } catch (error) {
          if (token !== this.selectionToken) return;
          this.setMediaViewerFeedback('error');
          this.updateVariant(null, optionValues);
          if (errorNode) {
            errorNode.textContent = this.dataset.loadErrorLabel;
            errorNode.hidden = false;
            const retry = document.createElement('button');
            retry.type = 'button';
            retry.className = 'text-link';
            retry.dataset.productStateRetry = '';
            retry.textContent = document.querySelector('[data-quick-view]')?.dataset.labelTryAgain || this.dataset.loadingLabel;
            errorNode.append(retry);
          }
        } finally {
          if (token === this.selectionToken) {
            this.selectionPending = false;
            this.removeAttribute('aria-busy');
            this.syncPurchaseState();
          }
        }
      }

      selectedColor(selectedOptions) {
        if (this.colorOptionIndex < 0) return '';
        if (selectedOptions) return selectedOptions[this.colorOptionIndex] || '';
        const fieldset = this.querySelector('[data-option-index="' + this.colorOptionIndex + '"]');
        const input = fieldset && fieldset.querySelector('input[type="radio"]:checked');
        return input ? input.value : '';
      }

      updateMobileColor(color) {
        if (!color || !this.mobileColorJump) return;
        const colorName = this.mobileColorJump.querySelector('[data-mobile-color-name]');
        const mobileSwatch = this.mobileColorJump.querySelector('[data-mobile-color-swatch]');
        const selectedInput = Array.from(this.querySelectorAll('.product-option--color input')).find((input) => input.value === color);
        const selectedSwatch = selectedInput && selectedInput.nextElementSibling
          ? selectedInput.nextElementSibling.querySelector('.product-option__swatch')
          : null;
        if (colorName) colorName.textContent = color;
        if (mobileSwatch && selectedSwatch) mobileSwatch.style.cssText = selectedSwatch.style.cssText;
        this.mobileColorJump.setAttribute(
          'aria-label',
          String(this.mobileColorJump.dataset.labelTemplate || '').replace('__COLOR__', color)
        );
      }

      onMobileColorJump() {
        this.scrollToColorSelection();
        const selectedInput = this.querySelector('.product-option--color input:checked');
        if (!selectedInput) return;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        clearTimeout(this.mobileFocusTimer);
        this.mobileFocusTimer = window.setTimeout(() => {
          if (this.isConnected && selectedInput.isConnected) selectedInput.focus({ preventScroll: true });
        }, reducedMotion ? 0 : 320);
      }

      updateMobileSize(options) {
        if (this.sizeOptionIndex < 0) return;
        const size = options[this.sizeOptionIndex] || '';
        this.section.querySelectorAll('[data-mobile-size-name]').forEach((node) => {
          node.textContent = size ? `${this.colorOptionIndex >= 0 ? ' · ' : ''}${size}` : '';
        });
        if (this.mobileColorJump && size) {
          const sizeField = this.querySelector('.product-option--size legend strong');
          this.mobileColorJump.setAttribute('aria-label', `${this.mobileColorJump.getAttribute('aria-label')?.split(' — ')[0] || ''} — ${sizeField?.textContent || ''}: ${size}`);
        }
      }

      scrollToColorSelection() {
        if (!this.mediaContainer || !window.matchMedia('(max-width: 749px)').matches) return;
        const mediaRect = this.mediaContainer.getBoundingClientRect();
        const header = document.querySelector('[data-site-header]');
        const safeTop = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const target = window.scrollY + mediaRect.top - safeTop - 8;
        window.scrollTo({ top: Math.max(0, target), behavior: reducedMotion ? 'auto' : 'smooth' });
      }


      mediaRoleFromAlt(value) {
        const segments = String(value || '').split('|').map((segment) => segment.trim()).filter(Boolean);
        if (segments.length < 2) return '';
        const role = segments.slice(1).find((segment) => !/^(?:display|length|inseam)\s*:/i.test(segment));
        return role ? role.replace(/^view\s*:\s*/i, '').trim() : '';
      }

      normalizeMediaRole(value) {
        return String(value || '')
          .trim()
          .toLocaleLowerCase()
          .replace(/^view\s*:\s*/i, '')
          .replace(/[\s_-]+/g, '');
      }


      bindMediaGallery() {
        if (!this.mediaContainer) return;
        this.mediaResize?.disconnect();
        this.mediaResize = new ResizeObserver(() => {
          const size = Math.ceil(this.mediaContainer.clientWidth) + 'px';
          this.mediaContainer.querySelectorAll('img[data-pdp-responsive]').forEach((image) => {
            if (image.sizes !== size) image.sizes = size;
          });
        });
        this.mediaResize.observe(this.mediaContainer);
        let frame = 0;
        this.listen(this.mediaContainer, 'scroll', () => {
          if (frame) return;
          frame = this.requestFrame(() => {
            frame = 0;
            this.updateMediaProgress();
          });
        }, { passive: true });
        this.listen(this.mediaContainer, 'keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const direction = event.key === 'ArrowLeft' ? -1 : 1;
          this.moveMedia(direction, 'keyboard');
        });
        if (this.mediaThumbnails) {
          this.listen(this.mediaThumbnails, 'click', (event) => {
            const thumbnail = event.target.closest('[data-media-thumbnail]');
            if (!thumbnail) return;
            const visibleItems = this.visibleMedia();
            const targetIndex = visibleItems.findIndex((item) => String(item.dataset.mediaId) === String(thumbnail.dataset.mediaThumbnail));
            if (targetIndex < 0) return;
            this.scrollToMedia(targetIndex, 'smooth', 'thumbnail');
          });
        }
        this.listen(this.mediaContainer, 'click', (event) => {
          const opener = event.target.closest('[data-product-media-open]');
          if (!opener) return;
          const item = opener.closest('[data-media-id]');
          if (item) this.openMediaViewer(item, opener);
        });
        let pointerStart = null;
        this.listen(this.mediaContainer, 'pointerdown', (event) => {
          if (event.pointerType === 'mouse') return;
          pointerStart = this.mediaContainer.scrollLeft;
        }, { passive: true });
        this.listen(this.mediaContainer, 'pointerup', () => {
          if (pointerStart === null) return;
          if (Math.abs(this.mediaContainer.scrollLeft - pointerStart) > 24) this.emitCarouselInteraction('swipe');
          pointerStart = null;
        }, { passive: true });
        this.listen(this.mediaContainer, 'scrollend', () => this.rememberMediaIntent(this.currentMediaIndex()));
      }

      visibleMedia() {
        return Array.from(this.section.querySelectorAll('[data-product-media] [data-media-id]')).filter((item) => !item.hidden);
      }

      visibleImageMedia() {
        return this.visibleMedia().filter((item) => item.dataset.mediaType === 'image');
      }

      currentMediaIndex(visibleItems = this.visibleMedia()) {
        if (!visibleItems.length) return 0;
        const containerLeft = this.mediaContainer.getBoundingClientRect().left;
        let currentIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;
        visibleItems.forEach((item, index) => {
          const distance = Math.abs(item.getBoundingClientRect().left - containerLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            currentIndex = index;
          }
        });
        return currentIndex;
      }

      updateMediaRatio(primaryItem, visibleItems = this.visibleMedia()) {
        if (!this.mediaContainer || !primaryItem) return;
        const sourceRatio = Number.parseFloat(primaryItem.dataset.mediaRatio);
        const ratio = Number.isFinite(sourceRatio) && sourceRatio > 0
          ? Math.min(1, Math.max(0.75, sourceRatio))
          : 0.8;
        if (this.gallery) this.gallery.style.setProperty('--product-media-ratio', String(ratio));
        this.applyConditionalMediaFit(visibleItems, ratio);
      }

      applyConditionalMediaFit(visibleItems, viewportRatio) {
        visibleItems.forEach((item, index) => {
          item.classList.remove('product-media__item--auto-fill');
          if (index === 0 || item.dataset.mediaType !== 'image' || item.classList.contains('product-media__item--fill')) return;
          const mediaRatio = Number.parseFloat(item.dataset.mediaRatio);
          if (!Number.isFinite(mediaRatio) || mediaRatio <= 0 || mediaRatio >= viewportRatio) return;
          const totalCrop = 1 - (mediaRatio / viewportRatio);
          const materiallyTaller = totalCrop >= 0.015;
          if (materiallyTaller && totalCrop <= 0.09) item.classList.add('product-media__item--auto-fill');
        });
      }

      scrollToMedia(index, behavior = 'smooth', source = '') {
        const visibleItems = this.visibleMedia();
        if (!visibleItems.length) return;
        const targetIndex = Math.min(visibleItems.length - 1, Math.max(0, index));
        const target = visibleItems[targetIndex];
        if (source) this.rememberMediaIntent(targetIndex);
        const targetImage = target.querySelector('img');
        if (targetImage) window.NorthyardProgressive?.upgrade(targetImage);
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.mediaContainer.scrollTo({ left: target.offsetLeft, behavior: reduceMotion ? 'instant' : behavior });
        this.requestFrame(() => this.updateMediaProgress(targetIndex));
        if (source) this.emitCarouselInteraction(source, targetIndex);
      }

      moveMedia(direction, source) {
        const visibleItems = this.visibleMedia();
        if (visibleItems.length < 2) return;
        this.scrollToMedia(this.currentMediaIndex(visibleItems) + direction, 'smooth', source);
      }

      emitCarouselInteraction(control, index = this.currentMediaIndex()) {
        const analytics = window.Northyard;
        if (!analytics || typeof analytics.emit !== 'function') return;
        analytics.emit('carousel_interaction', {
          product_id: this.dataset.productId,
          location: 'product_page',
          control,
          image_position: index + 1
        });
      }

      updateMediaProgress(forcedIndex) {
        const visibleItems = this.visibleMedia();
        const hasMultipleMedia = visibleItems.length > 1;
        if (this.mediaThumbnails) this.mediaThumbnails.hidden = !visibleItems.length;
        if (!visibleItems.length) {
          if (this.mediaStatus) this.mediaStatus.textContent = '';
          return;
        }
        const currentIndex = Number.isInteger(forcedIndex) ? forcedIndex : this.currentMediaIndex(visibleItems);
        if (this.mediaStatus) {
          this.mediaStatus.textContent = String(this.mediaStatus.dataset.statusTemplate || '')
            .replace('__CURRENT__', currentIndex + 1)
            .replace('__TOTAL__', visibleItems.length);
        }

        const thumbnails = this.mediaThumbnails ? Array.from(this.mediaThumbnails.querySelectorAll('[data-media-thumbnail]')) : [];
        thumbnails.forEach((thumbnail) => {
          const mediaId = String(thumbnail.dataset.mediaThumbnail);
          const visibleIndex = visibleItems.findIndex((item) => String(item.dataset.mediaId) === mediaId);
          thumbnail.setAttribute('aria-current', visibleIndex === currentIndex ? 'true' : 'false');
          if (visibleIndex === currentIndex && hasMultipleMedia) this.revealActiveThumbnail(thumbnail);
        });
        visibleItems.forEach((item, index) => {
          const opener = item.querySelector('[data-product-media-open]');
          if (opener) opener.setAttribute('aria-label', String(opener.dataset.labelTemplate || '').replace('__NUMBER__', index + 1));
        });
      }

      revealActiveThumbnail(thumbnail) {
        if (!this.mediaThumbnails || !thumbnail || !this.mediaThumbnails.clientWidth) return;
        const rail = this.mediaThumbnails.closest('product-thumbnail-rail');
        if (typeof rail?.revealThumbnail === 'function') {
          rail.revealThumbnail(thumbnail);
          return;
        }
        const vertical = getComputedStyle(this.mediaThumbnails).flexDirection === 'column';
        if (vertical) {
          const start = thumbnail.offsetTop;
          const end = start + thumbnail.offsetHeight;
          if (start < this.mediaThumbnails.scrollTop) this.mediaThumbnails.scrollTop = start;
          else if (end > this.mediaThumbnails.scrollTop + this.mediaThumbnails.clientHeight) {
            this.mediaThumbnails.scrollTop = end - this.mediaThumbnails.clientHeight;
          }
          return;
        }
        const start = thumbnail.offsetLeft;
        const end = start + thumbnail.offsetWidth;
        if (start < this.mediaThumbnails.scrollLeft) this.mediaThumbnails.scrollLeft = start;
        else if (end > this.mediaThumbnails.scrollLeft + this.mediaThumbnails.clientWidth) {
          this.mediaThumbnails.scrollLeft = end - this.mediaThumbnails.clientWidth;
        }
      }

      rebuildMediaThumbnails(visibleItems) {
        if (!this.mediaThumbnails) return;
        this.mediaThumbnails.replaceChildren();
        if (!visibleItems.length) {
          this.mediaThumbnails.hidden = true;
          return;
        }
        visibleItems.forEach((item, index) => {
          const thumbnail = document.createElement('button');
          thumbnail.className = 'product-gallery__thumbnail';
          thumbnail.type = 'button';
          thumbnail.dataset.mediaThumbnail = item.dataset.mediaId;
          thumbnail.setAttribute('aria-label', String(this.mediaThumbnails.dataset.labelTemplate || '').replace('__NUMBER__', index + 1));
          if (item.dataset.mediaThumbnailUrl) {
            const image = document.createElement('img');
            image.src = item.dataset.mediaThumbnailUrl;
            image.alt = '';
            image.loading = 'eager';
            image.decoding = 'async';
            thumbnail.append(image);
          }
          this.mediaThumbnails.append(thumbnail);
        });
        this.mediaThumbnails.hidden = false;
      }

      cancelMediaUpdate() {
        this.pendingMediaUpdate = null;
        this.mediaAbort?.abort();
        this.mediaAbort = null;
        this.mediaFeedback?.();
        this.mediaFeedback = null;
        this.setMediaViewerFeedback('ready');
        this.gallery?.removeAttribute('aria-busy');
      }

      startMediaUpdate(payload, token) {
        this.cancelMediaUpdate();
        this.pendingMediaUpdate = { payload, token };
        this.setMediaViewerFeedback();
        const controller = new AbortController();
        this.mediaAbort = controller;
        const selection = this.selectedOptions().filter((value, index) => index !== this.sizeOptionIndex).filter(Boolean).join(' · ') || payload.product.title;
        const labels = {};
        for (const state of ['loading', 'slow', 'error']) {
          const label = this.dataset['media' + state[0].toUpperCase() + state.slice(1)];
          if (label) labels[state] = label.replace('__SELECTION__', selection);
        }
        const finish = window.NorthyardImages?.wait(this.gallery?.querySelector('.product-gallery__stage'), {
          labels,
          onRetry: () => {
            if (controller.signal.aborted || token !== this.selectionToken || !this.isConnected) return;
            const active = document.activeElement;
            if (active?.closest('.ny-image-feedback')) {
              this.visibleMedia()[this.currentMediaIndex()]?.querySelector('[data-product-media-open]')?.focus({ preventScroll: true });
            }
            this.startMediaUpdate(this.payload, token);
          }
        });
        this.mediaFeedback = finish;
        this.updateMediaFromState(payload, token, { signal: controller.signal, onSlow: () => finish?.slow?.() })
          .then(() => {
            if (controller.signal.aborted || token !== this.selectionToken) return;
            finish?.();
            this.mediaFeedback = null;
            this.pendingMediaUpdate = null;
            this.setMediaViewerFeedback('ready');
          })
          .catch(() => {
            if (controller.signal.aborted || token !== this.selectionToken || !this.isConnected) return;
            this.gallery?.removeAttribute('aria-busy');
            finish?.error?.();
            this.setMediaViewerFeedback('error');
          });
      }

      async prepareMediaItem(item, options) {
        const prepare = window.NorthyardProgressive?.prepare || window.Northyard.ProductState.prepareImage;
        await prepare(item?.querySelector('img'), options);
      }

      rememberMediaIntent(index) {
        const item = this.visibleMedia()[index];
        if (!item || this.mediaIntent?.id === item.dataset.mediaId) return;
        this.mediaIntent = { id: item.dataset.mediaId, index };
        const pending = this.pendingMediaUpdate;
        if (pending) this.startMediaUpdate(pending.payload, pending.token);
      }

      async updateMediaFromState(payload, token, options = {}) {
        const oldItems = this.visibleMedia();
        const previousIndex = this.mediaIntent && oldItems[this.mediaIntent.index]?.dataset.mediaId === this.mediaIntent.id
          ? this.mediaIntent.index : this.currentMediaIndex(oldItems);
        const previousRole = this.normalizeMediaRole(oldItems[previousIndex]?.dataset.mediaRole);
        const media = payload.media || [];
        if (media.map((item) => String(item.id)).join(',') === oldItems.map((item) => item.dataset.mediaId).join(',')) return;
        let targetIndex = previousRole
          ? Math.max(0, media.findIndex(item => this.normalizeMediaRole(this.mediaRoleFromAlt(item.alt)) === previousRole))
          : previousIndex < media.length ? previousIndex : 0;
        const items = media.map((item, index) => {
          const node = document.createElement('div');
          node.className = 'product-media__item';
          if (/\|\s*display\s*:\s*fill\s*$/i.test(item.alt || '')) node.classList.add('product-media__item--fill');
          node.dataset.mediaId = item.id;
          node.dataset.mediaType = item.type;
          node.dataset.mediaAlt = item.alt || '';
          node.dataset.mediaRole = this.mediaRoleFromAlt(item.alt);
          node.dataset.mediaRatio = item.height ? item.width / item.height : .8;
          node.dataset.mediaThumbnailUrl = item.thumb || item.src;
          if (item.type === 'image') {
            const button = document.createElement('button');
            button.className = 'product-media__open';
            button.type = 'button';
            button.dataset.productMediaOpen = '';
            button.dataset.labelTemplate = this.mediaThumbnails.dataset.labelTemplate;
            const template = document.createElement('template');
            if (item.html) template.innerHTML = item.html;
            const image = template.content.querySelector('img') || document.createElement('img');
            image.fetchPriority = index === targetIndex ? 'high' : 'low';
            if (!image.getAttribute('src')) image.src = item.src;
            if (image.srcset) {
              image.dataset.pdpResponsive = '';
              image.sizes = Math.ceil(this.mediaContainer.clientWidth) + 'px';
            }
            image.alt = window.Northyard.ProductState.imageAlt(item, payload.product.title);
            image.width = item.width || 1200;
            image.height = item.height || 1600;
            image.loading = 'lazy';
            image.decoding = 'async';
            image.style.objectPosition = item.focal || '50% 50%';
            button.append(image);
            const expand = oldItems.find((entry) => entry.querySelector('.product-media__expand'))?.querySelector('.product-media__expand');
            if (expand) button.append(expand.cloneNode(true));
            node.append(button);
          } else if (item.html) {
            node.innerHTML = item.html;
          }
          return node;
        });
        this.gallery?.setAttribute('aria-busy', 'true');
        await this.prepareMediaItem(items[targetIndex], options);
        if (options.signal?.aborted || token !== this.selectionToken || !this.isConnected) return;
        items.forEach((item, index) => {
          const image = item.querySelector('img');
          if (image) window.NorthyardProgressive?.wrap(image, window.NorthyardProgressive.smallSource(image.src), index === targetIndex);
        });
        const restoreMediaFocus = oldItems.some((item) => item.contains(document.activeElement));
        this.pendingMediaUpdate = null;
        const emptyState = this.section.querySelector('[data-product-media-empty]');
        oldItems.forEach((item) => item.querySelectorAll('video').forEach((video) => video.pause()));
        this.mediaContainer.replaceChildren(...items);
        if (emptyState) {
          emptyState.hidden = items.length > 0;
          this.mediaContainer.append(emptyState);
        }
        if (items[0]) items[0].classList.add('is-color-primary');
        this.updateMediaRatio(items[0], items);
        window.NorthyardProgressive?.scan(this.mediaContainer);
        this.rebuildMediaThumbnails(items);
        this.scrollToMedia(targetIndex, 'instant');
        this.updateMediaProgress(targetIndex);
        this.gallery?.removeAttribute('aria-busy');
        if (this.mediaViewer?.open) {
          const imageItems = this.visibleImageMedia();
          const viewerIndex = imageItems.indexOf(items[targetIndex]);
          if (imageItems.length) {
            this.mediaViewerIndex = Math.max(0, viewerIndex);
            this.mediaViewerOpener = imageItems[this.mediaViewerIndex].querySelector('[data-product-media-open]');
            this.renderMediaViewer();
          } else {
            this.mediaViewer.close();
          }
        }
        if (restoreMediaFocus) items[targetIndex]?.querySelector('[data-product-media-open]')?.focus({ preventScroll: true });
      }

      setMediaViewerFeedback(state = 'loading') {
        this.mediaViewerFeedback?.();
        this.mediaViewerFeedback = null;
        if (state === 'ready' || !this.mediaViewer?.open) return;
        const selection = this.selectedOptions().filter((value, index) => index !== this.sizeOptionIndex).filter(Boolean).join(' · ') || this.payload.product.title;
        const labels = {};
        for (const status of ['loading', 'slow', 'error']) {
          const label = this.dataset['media' + status[0].toUpperCase() + status.slice(1)];
          if (label) labels[status] = label.replace('__SELECTION__', selection);
        }
        this.mediaViewerFeedback = window.NorthyardImages?.wait(this.mediaViewerStage, {
          labels,
          onRetry: () => {
            this.mediaViewer.querySelector('[data-product-media-viewer-close]')?.focus({ preventScroll: true });
            if (this.pendingMediaUpdate) this.startMediaUpdate(this.pendingMediaUpdate.payload, this.pendingMediaUpdate.token);
            else if (this.lastOptionInput) this.onOptionChange({ target: this.lastOptionInput, retry: true });
          }
        });
        if (state === 'error') this.mediaViewerFeedback?.error?.();
      }

      bindMediaViewer() {
        if (!this.mediaViewer) return;
        const close = this.mediaViewer.querySelector('[data-product-media-viewer-close]');
        this.listen(close, 'click', () => this.mediaViewer.close());
        this.listen(this.mediaViewerPrevious, 'click', () => this.moveMediaViewer(-1));
        this.listen(this.mediaViewerNext, 'click', () => this.moveMediaViewer(1));
        this.listen(this.mediaViewer, 'keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          this.moveMediaViewer(event.key === 'ArrowLeft' ? -1 : 1);
        });
        this.listen(this.mediaViewer, 'close', () => {
          if (this.mediaViewer.open) return;
          if (!document.querySelector('dialog[open]')) document.body.classList.remove('is-overlay-open');
          this.setMediaViewerFeedback('ready');
          if (this.mediaViewerStage) this.mediaViewerStage.replaceChildren();
          const opener = this.mediaViewerOpener?.isConnected ? this.mediaViewerOpener
            : this.visibleMedia()[this.currentMediaIndex()]?.querySelector('[data-product-media-open]') || this.lastOptionInput;
          opener?.focus({ preventScroll: true });
          this.mediaViewerOpener = null;
        });
      }

      openMediaViewer(item, opener) {
        if (!this.mediaViewer || typeof this.mediaViewer.showModal !== 'function') return;
        const imageItems = this.visibleImageMedia();
        const imageIndex = imageItems.indexOf(item);
        if (imageIndex < 0) return;
        this.mediaViewerIndex = imageIndex;
        this.mediaViewerOpener = opener;
        this.rememberMediaIntent(this.visibleMedia().indexOf(item));
        this.renderMediaViewer();
        document.body.classList.add('is-overlay-open');
        this.mediaViewer.showModal();
        if (this.selectionPending || this.pendingMediaUpdate) this.setMediaViewerFeedback();
      }

      moveMediaViewer(direction) {
        const imageItems = this.visibleImageMedia();
        if (imageItems.length < 2) return;
        this.mediaViewerIndex = Math.min(imageItems.length - 1, Math.max(0, this.mediaViewerIndex + direction));
        const item = imageItems[this.mediaViewerIndex];
        this.mediaViewerOpener = item.querySelector('[data-product-media-open]');
        const mediaIndex = this.visibleMedia().indexOf(item);
        this.rememberMediaIntent(mediaIndex);
        this.scrollToMedia(mediaIndex, 'instant');
        this.renderMediaViewer();
        this.emitCarouselInteraction(direction < 0 ? 'viewer_previous' : 'viewer_next', this.mediaViewerIndex);
      }

      renderMediaViewer() {
        if (!this.mediaViewerStage) return;
        this.setMediaViewerFeedback('ready');
        const imageItems = this.visibleImageMedia();
        const item = imageItems[this.mediaViewerIndex];
        const image = item && item.querySelector('img');
        this.mediaViewerStage.replaceChildren();
        if (image) {
          const viewerImage = image.cloneNode(true);
          window.NorthyardProgressive?.cleanClone(image, viewerImage);
          viewerImage.removeAttribute('loading');
          viewerImage.sizes = '100vw';
          viewerImage.style.objectPosition = 'center';
          this.mediaViewerStage.append(viewerImage);
          window.NorthyardProgressive?.wrap(viewerImage, window.NorthyardProgressive.smallSource(viewerImage.src), true);
        }
        if (this.mediaViewerCount) this.mediaViewerCount.textContent = `${this.mediaViewerIndex + 1} / ${imageItems.length}`;
        const hasMultipleImages = imageItems.length > 1;
        const controls = this.section.querySelector('[data-product-media-viewer-controls]');
        if (controls) controls.hidden = !hasMultipleImages;
        if (this.mediaViewerPrevious) this.mediaViewerPrevious.disabled = this.mediaViewerIndex === 0;
        if (this.mediaViewerNext) this.mediaViewerNext.disabled = this.mediaViewerIndex === imageItems.length - 1;
        if (this.selectionPending || this.pendingMediaUpdate) this.setMediaViewerFeedback();
      }

      updateAvailability(payload) {
        this.querySelectorAll('[data-option-index]').forEach((fieldset) => {
          const option = payload.options.find((item) => item.position === Number(fieldset.dataset.optionIndex) + 1);
          fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
            const value = option?.values.find((item) => String(item.id) === input.dataset.optionValueId);
            const available = Boolean(value?.available);
            const label = input.nextElementSibling;
            if (!label) return;
            label.classList.toggle('is-unavailable', !available);
            const unavailableText = label.querySelector('[data-option-unavailable]');
            if (unavailableText) unavailableText.hidden = available;
          });
        });
      }

      updateVariant(variant, optionValues) {
        const idInput = this.section.querySelector('[data-variant-id]');
        const buttons = this.section.querySelectorAll('[data-add-to-cart]');
        const prices = this.section.querySelectorAll('[data-product-price]');
        const compares = this.section.querySelectorAll('[data-product-compare]');
        const discounts = this.section.querySelectorAll('[data-product-discount]');
        const statuses = this.section.querySelectorAll('[data-product-status]');

        const url = new URL(window.location.href);
        url.searchParams.delete('option_values');
        if (variant) url.searchParams.set('variant', variant.id);
        else {
          url.searchParams.delete('variant');
          if (optionValues) url.searchParams.set('option_values', optionValues.join(','));
        }
        window.history.replaceState({}, '', url);
        if (!variant) {
          const alternatives = this.querySelector('[data-stock-alternatives]');
          if (alternatives) alternatives.hidden = true;
          prices.forEach((price) => { price.textContent = '—'; });
          compares.forEach((compare) => { compare.hidden = true; });
          discounts.forEach((discount) => { discount.hidden = true; });
          statuses.forEach((status) => {
            status.textContent = this.section.querySelector('[data-add-to-cart]').dataset.unavailableLabel;
            status.classList.add('product-status--sold-out');
          });
          idInput.value = '';
          buttons.forEach((button) => {
            button.disabled = true;
            button.textContent = button.dataset.unavailableLabel;
          });
          return;
        }

        idInput.value = variant.id;
        prices.forEach((price) => { price.textContent = variant.price_formatted; });
        compares.forEach((compare) => {
          compare.hidden = !variant.compare_formatted;
          compare.textContent = variant.compare_formatted || '';
        });
        const percentage = variant.compare_at_price > variant.price ? (((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100).toFixed(1).replace(/\.0$/, '') : '';
        discounts.forEach((discount) => {
          discount.hidden = !percentage;
          discount.textContent = percentage
            ? String(discount.dataset.discountTemplate || '').replace('__PERCENT__', percentage)
            : '';
        });
        buttons.forEach((button) => {
          button.disabled = !variant.available;
          button.textContent = variant.available ? button.dataset.addLabel : button.dataset.soldOutLabel;
        });
        statuses.forEach((status) => {
          status.textContent = variant.available ? status.dataset.inStock : status.dataset.soldOut;
          status.classList.toggle('product-status--sold-out', !variant.available);
        });
        this.syncPurchaseState();
      }

      updateStockAlternatives() {
        const node = this.querySelector('[data-stock-alternatives]');
        if (!node) return;
        if (this.selectionPending || !this.section.querySelector('[data-variant-id]')?.value) { node.hidden = true; return; }
        const options = this.selectedOptions();
        const selected = Object.fromEntries(this.payload.options.map(option => [option.name, options[option.position - 1]]));
        const markup = window.Northyard.ProductState.alternativesMarkup(this.payload, selected, this.dataset.productUrl, node.dataset);
        if (node.innerHTML !== markup) node.innerHTML = markup;
        node.hidden = !markup;
      }

      syncPurchaseState() {
        this.updateStockAlternatives();
        const commerce = window.Northyard;
        if (!commerce?.cartLabels) return;
        const variant = !this.selectionPending && this.section.querySelector('[data-variant-id]')?.value ? this.payload.variant : null;
        const busy = this.cartSubmitting || commerce.cartMutationPending || commerce.cartEditPending;
        this.section.querySelectorAll('[data-add-to-cart]').forEach(button => {
          button.disabled = !!(busy || (!commerce.cartUncertain && (this.selectionPending || !variant?.available)));
          button.textContent = busy ? button.dataset.addingLabel : commerce.cartUncertain ? commerce.cartLabels.view_cart
            : this.selectionPending ? this.dataset.loadingLabel : variant ? (variant.available ? button.dataset.addLabel : button.dataset.soldOutLabel) : button.dataset.unavailableLabel;
        });
        const error = this.form?.querySelector('[data-product-form-error]');
        if (!commerce.cartUncertain && error?.hasAttribute('data-cart-review-required')) {
          const drawer = document.querySelector('[data-cart-drawer]');
          if (error.contains(drawer?._northyardsOpener)) drawer._northyardsOpener = this.primaryAction?.querySelector('[data-add-to-cart]');
          error.hidden = true;
          error.removeAttribute('data-cart-review-required');
        }
      }

      async onSubmit(event) {
        const commerce = window.Northyard;
        if (!commerce || typeof commerce.cartRequest !== 'function' || typeof commerce.openCart !== 'function') return;
        event.preventDefault();
        if (commerce.cartUncertain) {
          await commerce.openCart(event.submitter || this.primaryAction?.querySelector('[data-add-to-cart]'));
          this.syncPurchaseState();
          return;
        }
        const idInput = this.section.querySelector('[data-variant-id]');
        const quantityInput = this.form.querySelector('[name="quantity"]');
        const errorNode = this.form.querySelector('[data-product-form-error]');
        const submitter = event.submitter || this.form.querySelector('[data-add-to-cart]');
        if (this.selectionPending || !idInput.value || !submitter || submitter.disabled) return;
        const requestedId = idInput.value;
        this.cartSubmitting = true;

        const buttons = this.section.querySelectorAll('[data-add-to-cart]');
        buttons.forEach((button) => {
          button.disabled = true;
          button.textContent = button.dataset.addingLabel;
        });
        if (errorNode) errorNode.hidden = true;

        try {
          const quantity = Math.max(1, Number(quantityInput && quantityInput.value) || 1);
          await commerce.cartRequest('/cart/add.js', {
            method: 'POST',
            body: JSON.stringify({ items: [{ id: Number(requestedId), quantity }] })
          });
          commerce.cartHighlightVariant = requestedId;
          await commerce.openCart(submitter);
        } catch (error) {
          if (errorNode) {
            errorNode.textContent = error.message;
            errorNode.hidden = false;
            if (error.needsReview || commerce.cartUncertain) {
              errorNode.setAttribute('data-cart-review-required', '');
              const review = document.createElement('a');
              review.href = commerce.path('/cart');
              review.className = 'text-link';
              review.setAttribute('data-cart-open', '');
              review.textContent = commerce.cartLabels.view_cart;
              errorNode.append(document.createTextNode(' '), review);
            }
          }
        } finally {
          this.cartSubmitting = false;
          this.syncPurchaseState();
        }
      }
    }
  );
}
